import * as React from 'react'
import { useRouter } from 'next/router'
import { ActionButtonYellow, LinkButton, PrimaryBlack, PrimaryYellow, SecondaryButton, SquareButton } from './Button.style'
import analytics from '../../../util/analytics-data-attributes/analyticsAttributes'
import type { Action, Category } from '../../../util/analytics-data-attributes/analyticsAttributes'

export enum Variants {
  primaryYellow = 'primary-yellow',
  primaryBlack = 'primary-black',
  secondary = 'secondary',
  link = 'link',
  buttonLink = 'buttonLink',
  actionYellow = 'action-yellow',
  square = 'square',
}

enum Sizes {
  medium = 'medium',
  large = 'large',
}

type Props = {
  children: React.ReactNode,
  className?: string,
  dataAnalyticsAction?: Action,
  dataAnalyticsCategory?: Category,
  dataAnalyticsLabel?: string,
  disabled?: boolean,
  forwardRef?: any,
  onClick: React.MouseEventHandler<HTMLButtonElement>,
  size?: Sizes,
  variant?: Variants,
  svgHeight?: number,
  dataGtm?: string,
  dataSlug?: string,
  htmlType?: 'button' | 'submit'
}

/**
 * Button element
 * @param {object} props Component props
 * @param {string} props.variant Button variant, primary, secondary, tertiary
 * @param {string} props.size Button medium, large
 * @param {function} props.onClick Button onClick handler
 * @param {object|string} props.children The content of the button
 * @param {boolean} props.disabled Button disabled state
 * @param {string} props.dataAnalyticsAction The action to be logged in analytics
 * @param {string} props.dataAnalyticsCategory The category to be logged in analytics
 * @param {string} props.dataAnalyticsLabel The label to be logged in analytics
 * @param {object} props.rest Additional optional attributes to be passed to button
 */

const Button = ({
  children,
  className,
  dataAnalyticsAction,
  dataAnalyticsCategory,
  dataAnalyticsLabel,
  disabled,
  onClick,
  size,
  variant,
  svgHeight,
  dataGtm,
  dataSlug,
  htmlType,
  ...rest
}: Props) => {
  const styleProps = {
    onClick,
    disabled,
    size,
    ...analytics({ 
      router: useRouter(),
      action: dataAnalyticsAction,
      category: dataAnalyticsCategory,
      label: dataAnalyticsLabel 
    }),
    className,
    svgHeight,
    ...rest
  }

  switch (variant) {
    case Button.Variants.secondary:
      return <SecondaryButton
        data-gtm={dataGtm}
        data-slug={dataSlug}
        {...styleProps}
        type={htmlType}
      >{children}</SecondaryButton>

    case Button.Variants.link:
      return <LinkButton
        data-gtm={dataGtm}
        data-slug={dataSlug}
        {...styleProps}
      >{children}</LinkButton>

    case Button.Variants.buttonLink:
        return <LinkButton
        data-gtm={dataGtm}
        data-slug={dataSlug}
        {...styleProps}
        linkStyle={'button'}>{children}</LinkButton>
  
    case Button.Variants.primaryBlack:
      return <PrimaryBlack
        data-gtm={dataGtm}
        data-slug={dataSlug}
        {...styleProps}
        type={htmlType}
      >{children}</PrimaryBlack>

    case Button.Variants.actionYellow:
      return <ActionButtonYellow
        data-gtm={dataGtm}
        data-slug={dataSlug}
        {...styleProps}
        type={htmlType}
      >{children}</ActionButtonYellow>

    case Button.Variants.square:
      return <SquareButton
        data-gtm={dataGtm}
        data-slug={dataSlug}
        {...styleProps}
        type={htmlType}
      >{children}</SquareButton>
  
    case Button.Variants.primaryYellow:
    default:
      return <PrimaryYellow
        data-gtm={dataGtm}
        data-slug={dataSlug}
        {...styleProps}
        type={htmlType}
      >{children}</PrimaryYellow>
  }
}

Button.Variants = Variants

Button.Sizes = Sizes

Button.defaultProps = {
  className: undefined,
  dataAnalyticsAction: undefined,
  dataAnalyticsCategory: undefined,
  dataAnalyticsLabel: undefined,
  disabled: false,
  size: Sizes.medium,
  variant: Variants.primaryYellow,
}

export default Button
